import dayjs from 'dayjs';
import { useEffect, useMemo, useRef } from 'react';
import { Tag, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { THistoryMeeting } from '@/common/basis/types/database/thistory-meeting';
import { TField } from '@/common/basis/types/tfield';
import { openSummary } from '@/common/utils/window/webview';
import { useMeetingType } from '@/common/hooks/meeting-type';

function App(props: {
  index: number;
  historyList: THistoryMeeting.Item[];
  setCurrentHistoryIndex: (currentHistoryIndex: number) => void;
  setIsOpenHistroyDetail: (isOpenHistroyDetail: boolean) => void;
}) {
  const index = props.index;
  const historyList = props.historyList;
  const setCurrentHistoryIndex = props.setCurrentHistoryIndex;
  const setIsOpenHistroyDetail = props.setIsOpenHistroyDetail;

  const item = useMemo(() => historyList[index], [historyList, index]);
  const roomId = useMemo(() => item?.roomId, [item?.roomId]);
  const roomSid = useMemo(() => item?.roomSid ?? '', [item?.roomSid]);
  const finishedTime = useMemo(() => item?.finishedTime, [item?.finishedTime]);
  const startedTime = useMemo(() => item?.startedTime, [item?.startedTime]);
  const repeatType = useMemo(() => item?.repeatType, [item?.repeatType]);
  const roomName = useMemo(() => item?.roomName, [item?.roomName]);
  const existRecordFile = useMemo(() => item?.existRecordFile, [item?.existRecordFile]);
  const meetingType = useMemo(() => item?.meetingType ?? TField.MEETING_TYPE_QUICK, [item?.meetingType]);
  const { isMeetingLive } = useMeetingType(meetingType);
  const { t } = useTranslation();
  const isRepeatMeeting = useMemo(() => repeatType && repeatType !== TField.MEET_REPEAT_TYPE_NONE, [repeatType]);
  const repeatStr = useMemo(() => (isRepeatMeeting ? t('cycle') : ''), [isRepeatMeeting]);
  const themeToken = theme.useToken();
  const isClick = useRef(false);
  const isClickTimerId = useRef<NodeJS.Timeout | null>(null);
  const isSameDay = dayjs(startedTime).isSame(dayjs(finishedTime), 'day');

  useEffect(() => {
    return () => {
      if (isClickTimerId.current) {
        clearTimeout(isClickTimerId.current);
      }
    };
  }, []);

  return (
    <div
      className='mb-3 cursor-pointer'
      onClick={() => {
        setIsOpenHistroyDetail(true);
        setCurrentHistoryIndex(index);
      }}
    >
      <div className='flex min-h-14 items-center justify-between rounded-md bg-[rgba(19,20,21,.04)] py-[7px]'>
        <div className='h-8 overflow-hidden px-3'>
          {isMeetingLive ? (
            <img className='h-8 w-8' src={require('@/common/basis/assets/live_finish_icon.png')} />
          ) : (
            <img className='h-8 w-8' src={require('@/common/basis/assets/video_finish_icon.png')} />
          )}
        </div>
        <div className='flex flex-1 items-center justify-between'>
          <div>
            <div className='flex h-5 items-center justify-start'>
              <div className='line-clamp-1 overflow-hidden text-[15px]' style={{ wordBreak: 'break-word' }}>
                <span>{roomName}</span>
              </div>
              {repeatStr ? (
                <Tag className='ml-2 flex h-[18px] items-center justify-center px-1 text-[12px]' color='blue'>
                  {repeatStr}
                </Tag>
              ) : (
                <></>
              )}
            </div>
            <div className='mt-1 flex items-center justify-start text-[12px] text-[#aaa]'>
              <div className='flex items-center justify-start'>
                <span>{dayjs(startedTime).format(isSameDay ? 'HH:mm' : 'MM-DD HH:mm')}</span>
                <span className='mx-1'>-</span>
                <span>{dayjs(finishedTime).format(isSameDay ? 'HH:mm' : 'MM-DD HH:mm')}</span>
              </div>
              <div className='mx-2 h-[10px] w-[1px] overflow-hidden bg-[rgba(19,20,21,.22)]' />
              <div>
                <span>{roomId}</span>
              </div>
            </div>
            {existRecordFile ? (
              <>
                <div
                  className='pt-1 text-[12px]'
                  onClick={(evt) => {
                    if (isClick.current) {
                      return;
                    }
                    isClick.current = true;
                    openSummary({ roomId: roomId ?? '', roomSid });
                    evt.stopPropagation();
                    isClickTimerId.current = setTimeout(() => (isClick.current = false), 5000);
                  }}
                  style={{ color: themeToken.token.colorPrimary }}
                >
                  <span>{t('recordingClickToView')}</span>
                </div>
              </>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
